/**
 * Offline Storage Utility
 * IndexedDB wrapper for storing critical data offline
 */

const DB_NAME = 'PaceEstateDB';
const DB_VERSION = 1;

// Store names
const STORES = {
  USER_PROFILE: 'userProfile',
  INVENTORY: 'inventory',
  SALES: 'sales',
  LEADS: 'leads',
  PENDING_REQUESTS: 'pendingRequests',
  CACHE_METADATA: 'cacheMetadata'
};

/**
 * Initialize IndexedDB
 */
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create object stores if they don't exist
      if (!db.objectStoreNames.contains(STORES.USER_PROFILE)) {
        db.createObjectStore(STORES.USER_PROFILE, { keyPath: 'id' });
      }

      if (!db.objectStoreNames.contains(STORES.INVENTORY)) {
        const inventoryStore = db.createObjectStore(STORES.INVENTORY, { keyPath: 'id' });
        inventoryStore.createIndex('sale_id', 'sale_id', { unique: false });
        inventoryStore.createIndex('updated_at', 'updated_at', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.SALES)) {
        const salesStore = db.createObjectStore(STORES.SALES, { keyPath: 'id' });
        salesStore.createIndex('status', 'status', { unique: false });
        salesStore.createIndex('updated_at', 'updated_at', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.LEADS)) {
        const leadsStore = db.createObjectStore(STORES.LEADS, { keyPath: 'id' });
        leadsStore.createIndex('status', 'status', { unique: false });
        leadsStore.createIndex('updated_at', 'updated_at', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.PENDING_REQUESTS)) {
        const pendingStore = db.createObjectStore(STORES.PENDING_REQUESTS, {
          keyPath: 'id',
          autoIncrement: true
        });
        pendingStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.CACHE_METADATA)) {
        db.createObjectStore(STORES.CACHE_METADATA, { keyPath: 'key' });
      }
    };
  });
};

/**
 * Generic get operation
 */
const get = async (storeName, key) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Generic getAll operation
 */
const getAll = async (storeName, indexName = null, query = null) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);

    let request;
    if (indexName) {
      const index = store.index(indexName);
      request = query ? index.getAll(query) : index.getAll();
    } else {
      request = store.getAll();
    }

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Generic put operation
 */
const put = async (storeName, data) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(data);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Generic delete operation
 */
const remove = async (storeName, key) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all data from a store
 */
const clear = async (storeName) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * User Profile Operations
 */
export const userProfile = {
  get: () => get(STORES.USER_PROFILE, 'current'),
  save: (profile) => put(STORES.USER_PROFILE, { ...profile, id: 'current' }),
  clear: () => clear(STORES.USER_PROFILE)
};

/**
 * Inventory Operations
 */
export const inventory = {
  get: (id) => get(STORES.INVENTORY, id),
  getAll: () => getAll(STORES.INVENTORY),
  getBySale: (saleId) => getAll(STORES.INVENTORY, 'sale_id', saleId),
  getRecent: async (limit = 50) => {
    const items = await getAll(STORES.INVENTORY);
    return items
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      .slice(0, limit);
  },
  save: (item) => put(STORES.INVENTORY, { ...item, updated_at: new Date().toISOString() }),
  saveMany: async (items) => {
    const timestamp = new Date().toISOString();
    for (const item of items) {
      await put(STORES.INVENTORY, { ...item, updated_at: timestamp });
    }
  },
  remove: (id) => remove(STORES.INVENTORY, id),
  clear: () => clear(STORES.INVENTORY)
};

/**
 * Sales Operations
 */
export const sales = {
  get: (id) => get(STORES.SALES, id),
  getAll: () => getAll(STORES.SALES),
  getByStatus: (status) => getAll(STORES.SALES, 'status', status),
  getActive: async () => {
    const allSales = await getAll(STORES.SALES);
    return allSales.filter(s => ['planning', 'active', 'scheduled'].includes(s.status));
  },
  save: (sale) => put(STORES.SALES, { ...sale, updated_at: new Date().toISOString() }),
  saveMany: async (salesList) => {
    const timestamp = new Date().toISOString();
    for (const sale of salesList) {
      await put(STORES.SALES, { ...sale, updated_at: timestamp });
    }
  },
  remove: (id) => remove(STORES.SALES, id),
  clear: () => clear(STORES.SALES)
};

/**
 * Leads Operations
 */
export const leads = {
  get: (id) => get(STORES.LEADS, id),
  getAll: () => getAll(STORES.LEADS),
  getByStatus: (status) => getAll(STORES.LEADS, 'status', status),
  save: (lead) => put(STORES.LEADS, { ...lead, updated_at: new Date().toISOString() }),
  remove: (id) => remove(STORES.LEADS, id),
  clear: () => clear(STORES.LEADS)
};

/**
 * Pending Requests Queue
 */
export const pendingRequests = {
  add: async (request) => {
    const timestamp = Date.now();
    return put(STORES.PENDING_REQUESTS, { ...request, timestamp });
  },
  getAll: () => getAll(STORES.PENDING_REQUESTS),
  remove: (id) => remove(STORES.PENDING_REQUESTS, id),
  clear: () => clear(STORES.PENDING_REQUESTS)
};

/**
 * Cache Metadata Operations
 */
export const cacheMetadata = {
  get: (key) => get(STORES.CACHE_METADATA, key),
  set: (key, value) => put(STORES.CACHE_METADATA, { key, value, timestamp: Date.now() }),
  remove: (key) => remove(STORES.CACHE_METADATA, key),
  clear: () => clear(STORES.CACHE_METADATA)
};

/**
 * Get total storage usage estimate
 */
export const getStorageEstimate = async () => {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    return await navigator.storage.estimate();
  }
  return null;
};

/**
 * Clear all offline data
 */
export const clearAllData = async () => {
  await userProfile.clear();
  await inventory.clear();
  await sales.clear();
  await leads.clear();
  await pendingRequests.clear();
  await cacheMetadata.clear();
};

/**
 * Sync data from server to local storage
 */
export const syncFromServer = async (data) => {
  if (data.user) {
    await userProfile.save(data.user);
  }
  if (data.inventory) {
    await inventory.saveMany(data.inventory);
  }
  if (data.sales) {
    await sales.saveMany(data.sales);
  }
  if (data.leads) {
    await leads.saveMany(data.leads);
  }
  await cacheMetadata.set('lastSync', new Date().toISOString());
};

/**
 * Get last sync timestamp
 */
export const getLastSync = async () => {
  const metadata = await cacheMetadata.get('lastSync');
  return metadata?.value || null;
};

export default {
  userProfile,
  inventory,
  sales,
  leads,
  pendingRequests,
  cacheMetadata,
  getStorageEstimate,
  clearAllData,
  syncFromServer,
  getLastSync
};
